<template>
  <div class="register-page">
    <div class="register-container">
      <!-- Left Panel - Branding -->
      <div class="brand-panel">
        <div class="brand-content">
          <div class="logo-box">
            <img src="@/assets/logo.png" alt="ZENTRY" class="logo" />
          </div>

          <h1 class="brand-title">Registrate</h1>
          <p class="brand-subtitle">Comienza a automatizar tu gestión documental hoy mismo</p>

          <div class="features">
            <div class="feature">
              <div class="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </div>
              <div class="feature-text">
                <h3>Registro Gratuito</h3>
                <p>Sin tarjeta de crédito, sin compromisos</p>
              </div>
            </div>

            <div class="feature">
              <div class="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                    stroke="currentColor" stroke-width="2" />
                  <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
              </div>
              <div class="feature-text">
                <h3>Configuración Rápida</h3>
                <p>En menos de 5 minutos estarás listo</p>
              </div>
            </div>

            <div class="feature">
              <div class="feature-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 15C15.866 15 19 11.866 19 8C19 4.13401 15.866 1 12 1C8.13401 1 5 4.13401 5 8C5 11.866 8.13401 15 12 15Z"
                    stroke="currentColor" stroke-width="2" />
                  <path d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <div class="feature-text">
                <h3>Soporte Premium</h3>
                <p>Asistencia técnica incluida</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Panel - Register Form o Success Message -->
      <div class="form-panel">
        <div class="form-wrapper">
          <!-- Estado: Registro exitoso - Verificar email -->
          <div v-if="registrationSuccess" class="success-container">
            <div class="success-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#06b6d4" stroke-width="2" />
                <path d="M9 12L11 14L15 10" stroke="#06b6d4" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </div>
            <h2 class="success-title">¡Registro Exitoso!</h2>
            <p class="success-message">
              Hemos enviado un correo de verificación a:
            </p>
            <p class="success-email">{{ registeredEmail }}</p>

            <div class="success-steps">
              <h3>Próximos pasos:</h3>
              <ol>
                <li>
                  <span class="step-number">1</span>
                  <span class="step-text">Revisa tu bandeja de entrada (y spam)</span>
                </li>
                <li>
                  <span class="step-number">2</span>
                  <span class="step-text">Haz clic en el enlace de verificación</span>
                </li>
                <li>
                  <span class="step-number">3</span>
                  <span class="step-text">Tu cuenta se activará automáticamente</span>
                </li>
                <li>
                  <span class="step-number">4</span>
                  <span class="step-text">Inicia sesión con tu nueva cuenta</span>
                </li>
              </ol>
            </div>

            <div class="success-actions">
              <button @click="resendVerification" class="btn-secondary" :disabled="resendLoading || resendCooldown > 0">
                <span v-if="resendLoading">Enviando...</span>
                <span v-else-if="resendCooldown > 0">Reenviar en {{ resendCooldown }}s</span>
                <span v-else>Reenviar correo de verificación</span>
              </button>
              <router-link to="/login" class="btn-text">
                Ir a Iniciar Sesión
              </router-link>
            </div>

            <p v-if="resendSuccess" class="resend-success">
              ✓ Correo reenviado exitosamente
            </p>
          </div>

          <!-- Estado: Formulario de registro -->
          <template v-else>
            <div class="form-header">
              <h2>Crear Cuenta</h2>
              <p>Completa tus datos para registrarte</p>
            </div>

            <form @submit.prevent="handleRegister" class="register-form">
              <div class="form-row">
                <div class="form-group">
                  <label for="firstName">Nombre</label>
                  <input id="firstName" v-model="form.firstName" type="text" placeholder="Tu nombre" required
                    :class="{ 'error': errors.firstName }">
                  <span v-if="errors.firstName" class="error-message">{{ errors.firstName }}</span>
                </div>

                <div class="form-group">
                  <label for="lastName">Apellido</label>
                  <input id="lastName" v-model="form.lastName" type="text" placeholder="Tu apellido" required
                    :class="{ 'error': errors.lastName }">
                  <span v-if="errors.lastName" class="error-message">{{ errors.lastName }}</span>
                </div>
              </div>

              <div class="form-group">
                <label for="email">Correo electrónico</label>
                <input id="email" v-model="form.email" type="email" placeholder="ejemplo@empresa.com" required
                  :class="{ 'error': errors.email }">
                <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
              </div>

              <div class="form-group">
                <label for="password">Contraseña</label>
                <div class="password-input">
                  <input id="password" v-model="form.password" :type="showPassword ? 'text' : 'password'"
                    placeholder="Mínimo 8 caracteres" required :class="{ 'error': errors.password }">
                  <button type="button" class="password-toggle" @click="showPassword = !showPassword">
                    <svg v-if="showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M17.94 17.94A10.07 10.07 0 0 1 12 20C7 20 2.73 16.39 1 12A18.45 18.45 0 0 1 5.06 5.06L17.94 17.94Z"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M1 1L23 23" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4C17 4 21.27 7.61 23 12A18.5 18.5 0 0 1 19.9 16.76"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round" />
                      <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" />
                    </svg>
                  </button>
                </div>
                <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
              </div>

              <div class="form-group">
                <label for="confirmPassword">Confirmar Contraseña</label>
                <div class="password-input">
                  <input id="confirmPassword" v-model="form.confirmPassword"
                    :type="showConfirmPassword ? 'text' : 'password'" placeholder="Repite tu contraseña" required
                    :class="{ 'error': errors.confirmPassword }">
                  <button type="button" class="password-toggle" @click="showConfirmPassword = !showConfirmPassword">
                    <svg v-if="showConfirmPassword" width="20" height="20" viewBox="0 0 24 24" fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M17.94 17.94A10.07 10.07 0 0 1 12 20C7 20 2.73 16.39 1 12A18.45 18.45 0 0 1 5.06 5.06L17.94 17.94Z"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M1 1L23 23" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4C17 4 21.27 7.61 23 12A18.5 18.5 0 0 1 19.9 16.76"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round" />
                      <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" />
                    </svg>
                  </button>
                </div>
                <span v-if="errors.confirmPassword" class="error-message">{{ errors.confirmPassword }}</span>
              </div>

              <div class="form-options">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="form.acceptTerms" required>
                  <span class="checkmark"></span>
                  Acepto los <a href="#" class="terms-link"> términos y condiciones</a>
                </label>
              </div>

              <!-- Error general -->
              <div v-if="errors.general" class="alert alert-error">
                {{ errors.general }}
              </div>

              <button type="submit" class="btn-primary" :disabled="loading">
                <span v-if="loading" class="spinner"></span>
                {{ loading ? 'Creando cuenta...' : 'Crear Cuenta' }}
              </button>
            </form>

            <div class="form-footer">
              <p>¿Ya tienes cuenta? <router-link to="/login" class="signin-link">Inicia sesión</router-link></p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth'
import { authService } from '@/services/api'

export default {
  name: 'Register',
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        acceptTerms: false
      },
      showPassword: false,
      showConfirmPassword: false,
      loading: false,
      errors: {},
      // Nuevos estados para el flujo de verificación
      registrationSuccess: false,
      registeredEmail: '',
      resendLoading: false,
      resendSuccess: false,
      resendCooldown: 0
    }
  },
  mounted() {
    if (this.$route.query.email) {
      this.form.email = this.$route.query.email
    }
  },
  setup() {
    const authStore = useAuthStore()
    return { authStore }
  },
  methods: {
    validateForm() {
      this.errors = {}

      if (!this.form.firstName.trim()) {
        this.errors.firstName = 'El nombre es requerido'
      }

      if (!this.form.lastName.trim()) {
        this.errors.lastName = 'El apellido es requerido'
      }

      if (!this.form.email.trim()) {
        this.errors.email = 'El correo electrónico es requerido'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
        this.errors.email = 'El formato del correo electrónico no es válido'
      }

      if (!this.form.password) {
        this.errors.password = 'La contraseña es requerida'
      } else if (this.form.password.length < 8) {
        this.errors.password = 'La contraseña debe tener al menos 8 caracteres'
      }

      if (!this.form.confirmPassword) {
        this.errors.confirmPassword = 'Confirma tu contraseña'
      } else if (this.form.password !== this.form.confirmPassword) {
        this.errors.confirmPassword = 'Las contraseñas no coinciden'
      }

      if (!this.form.acceptTerms) {
        this.errors.general = 'Debes aceptar los términos y condiciones'
      }

      return Object.keys(this.errors).length === 0
    },

    async handleRegister() {
      if (!this.validateForm()) {
        return
      }

      this.loading = true

      try {
        // Preparar datos para el backend
        const userData = {
          name: `${this.form.firstName} ${this.form.lastName}`,
          email: this.form.email,
          password: this.form.password,
          password_confirmation: this.form.confirmPassword,
          user_type: 'empresa'
        }

        const response = await this.authStore.register(userData)

        if (response.success) {
          // Registro exitoso - mostrar pantalla de verificación
          this.registrationSuccess = true
          this.registeredEmail = response.data?.email || this.form.email
        } else {
          // Mostrar errores específicos
          if (response.errors) {
            // Mapear errores del backend a nuestros campos
            if (response.errors.email) {
              this.errors.email = response.errors.email[0]
            }
            if (response.errors.name) {
              this.errors.firstName = response.errors.name[0]
            }
            if (response.errors.password) {
              this.errors.password = response.errors.password[0]
            }

            // Si hay otros errores, mostrarlos como error general
            if (!this.errors.email && !this.errors.firstName && !this.errors.password) {
              this.errors.general = response.message
            }
          } else {
            this.errors.general = response.message || 'Error al crear la cuenta'
          }
        }
      } catch (error) {
        this.errors.general = 'Error de conexión. Inténtalo de nuevo.'
      } finally {
        this.loading = false
      }
    },

    async resendVerification() {
      if (this.resendCooldown > 0) return

      this.resendLoading = true
      this.resendSuccess = false

      try {
        const response = await authService.resendVerificationEmail(this.registeredEmail)

        if (response.success) {
          this.resendSuccess = true
          // Iniciar cooldown de 60 segundos
          this.resendCooldown = 60
          const interval = setInterval(() => {
            this.resendCooldown--
            if (this.resendCooldown <= 0) {
              clearInterval(interval)
              this.resendSuccess = false
            }
          }, 1000)
        } else {
          this.errors.general = response.message || 'Error al reenviar el correo'
        }
      } catch (error) {
        this.errors.general = 'Error de conexión'
      } finally {
        this.resendLoading = false
      }
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.register-page {
  height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  display: flex;
  align-items: stretch;
  justify-content: center;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.register-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  height: 100%;
  background: white;
  overflow: hidden;
}

/* Brand Panel */
.brand-panel {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%);
  padding: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  overflow: hidden;
}

.brand-panel::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: pulse 15s ease-in-out infinite;
}

@keyframes pulse {

  0%,
  100% {
    transform: scale(1);
    opacity: 0.5;
  }

  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

.brand-content {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 500px;
  text-align: center;
}

.logo-box {
  background: white;
  padding: 20px 30px;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  margin-bottom: 24px;
  display: inline-block;
  transition: transform 0.3s ease;
}

.logo-box:hover {
  transform: translateY(-5px);
}

.logo {
  max-width: 30vw;
  height: auto;
  display: block;
}

.brand-title {
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 10px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.brand-subtitle {
  font-size: 0.95rem;
  opacity: 0.95;
  line-height: 1.4;
  margin-bottom: 28px;
}

.features {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.feature {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  text-align: left;
}

.feature:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(10px);
}

.feature-icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0891b2;
}

.feature-icon svg {
  width: 20px;
  height: 20px;
}

.feature-text h3 {
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 3px;
}

.feature-text p {
  font-size: 0.8rem;
  opacity: 0.9;
  line-height: 1.3;
}

/* Form Panel */
.form-panel {
  padding: 30px 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  overflow: hidden;
}

.form-wrapper {
  width: 100%;
  max-width: 450px;
}

.form-header {
  margin-bottom: 20px;
  text-align: center;
}

.form-header h2 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.form-header p {
  color: #64748b;
  font-size: 0.9rem;
}

.register-form {
  margin-bottom: 18px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
}

.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
  font-size: 0.85rem;
  letter-spacing: 0.3px;
}

.form-group input {
  width: 100%;
  padding: 11px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: #f8fafc;
  color: #0f172a;
  font-family: inherit;
}

.form-group input:focus {
  outline: none;
  border-color: #0891b2;
  background: white;
  box-shadow: 0 0 0 4px rgba(8, 145, 178, 0.1);
  transform: translateY(-2px);
}

.form-group input::placeholder {
  color: #94a3b8;
}

.form-group input.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.password-input {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle:hover {
  color: #0891b2;
  background: #f1f5f9;
}

.error-message {
  color: #ef4444;
  font-size: 0.8rem;
  margin-top: 4px;
  display: block;
  font-weight: 500;
}

.form-options {
  margin-bottom: 16px;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  font-size: 0.85rem;
  color: #475569;
  line-height: 1.4;
  font-weight: 500;
}

.checkbox-label input {
  margin-right: 8px;
  margin-top: 2px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #0891b2;
}

.terms-link {
  color: #0891b2;
  text-decoration: none;
  font-weight: 600;
}

.terms-link:hover {
  color: #0e7490;
  text-decoration: underline;
}

.alert {
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 16px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid;
}

.alert-error {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-color: #fca5a5;
  color: #991b1b;
}

.btn-primary {
  width: 100%;
  padding: 13px 24px;
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(6, 182, 212, 0.4);
  letter-spacing: 0.3px;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(6, 182, 212, 0.5);
}

.btn-primary:active:not(:disabled) {
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 10px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.form-footer {
  text-align: center;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 2px solid #f1f5f9;
}

.form-footer p {
  color: #64748b;
  margin: 0;
  font-size: 0.85rem;
}

.signin-link {
  color: #0891b2;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;
}

.signin-link:hover {
  color: #0e7490;
  border-bottom-color: #0e7490;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .brand-title {
    font-size: 1.65rem;
  }

  .brand-subtitle {
    font-size: 0.95rem;
  }

  .form-header h2 {
    font-size: 1.65rem;
  }
}

@media (max-width: 968px) {
  .register-container {
    grid-template-columns: 1fr;
  }

  .brand-panel {
    display: none;
  }

  .form-panel {
    padding: 40px 30px;
  }
}

@media (max-width: 640px) {
  .form-panel {
    padding: 30px 24px;
  }

  .form-header {
    margin-bottom: 20px;
  }

  .form-header h2 {
    font-size: 1.5rem;
  }

  .form-header p {
    font-size: 0.875rem;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .form-group {
    margin-bottom: 12px;
  }

  .btn-primary {
    padding: 13px 20px;
    font-size: 0.95rem;
  }
}

@media (max-width: 480px) {
  .form-panel {
    padding: 24px 20px;
  }

  .form-header h2 {
    font-size: 1.35rem;
  }

  .logo {
    max-width: 150px;
  }
}

/* Success Container Styles */
.success-container {
  text-align: center;
  padding: 20px;
}

.success-icon {
  margin-bottom: 24px;
  animation: scaleIn 0.5s ease;
  display: flex !important;
  justify-content: center !important;
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.success-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 12px;
}

.success-message {
  color: #64748b;
  font-size: 1rem;
  margin-bottom: 8px;
}

.success-email {
  font-size: 1.1rem;
  font-weight: 600;
  color: #06b6d4;
  margin-bottom: 32px;
}

.success-steps {
  text-align: left;
  background: #f8fafc;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
}

.success-steps h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 16px;
}

.success-steps ol {
  list-style: none;
  padding: 0;
  margin: 0;
}

.success-steps li {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.success-steps li:last-child {
  margin-bottom: 0;
}

.step-number {
  width: 28px;
  height: 28px;
  background: #06b6d4;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
  flex-shrink: 0;
}

.step-text {
  color: #475569;
  font-size: 0.95rem;
}

.success-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn-secondary {
  width: 100%;
  padding: 14px 24px;
  background: #f1f5f9;
  color: #0f172a;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover:not(:disabled) {
  background: #e2e8f0;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-text {
  color: #06b6d4;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.95rem;
  padding: 12px;
  transition: color 0.2s;
}

.btn-text:hover {
  color: #0891b2;
}

.resend-success {
  color: #10b981;
  font-size: 0.9rem;
  margin-top: 16px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
